import { useEffect, useState, useMemo } from "react";
import { Link } from "react-router-dom";
import { fetchPackages, postEstimate } from "@/lib/api";
import { useSimulation } from "@/context/SimulationContext";
import { fmtRp, fmtNum } from "@/lib/format";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Label } from "@/components/ui/label";
import { AlertTriangle, CheckCircle2, Settings, Boxes, MessageCircle, Sprout } from "lucide-react";
import { toast } from "sonner";

export default function Konfigurator() {
  const { selectedTier, input } = useSimulation();
  const [packages, setPackages] = useState([]);
  const [va, setVa] = useState(input.daya_pln_lain || input.daya_pln_va || 3500);
  const [tier, setTier] = useState(selectedTier || "Safer");
  const [panels, setPanels] = useState(null);
  const [batteries, setBatteries] = useState(null);
  const [estimate, setEstimate] = useState(null);
  const [loading, setLoading] = useState(false);
  const [mode, setMode] = useState("guided");

  useEffect(() => { fetchPackages().then((d) => setPackages(d.packages || [])); }, []);

  const availableVas = useMemo(() => [...new Set(packages.map((p) => p.va))].sort((a, b) => a - b), [packages]);
  const currentPkg = useMemo(() => packages.find((p) => p.va === Number(va) && p.tier === tier), [packages, va, tier]);

  useEffect(() => {
    if (currentPkg) {
      setPanels(currentPkg.panels);
      setBatteries(currentPkg.battery_units);
    }
  }, [currentPkg]);

  const runEstimate = async () => {
    if (!currentPkg) return;
    setLoading(true);
    try {
      const r = await postEstimate({
        package_id: currentPkg.id,
        panel_count_override: panels,
        battery_count_override: batteries,
      });
      setEstimate(r);
    } catch (e) {
      toast.error("Gagal estimasi: " + (e?.response?.data?.detail || e.message));
    } finally { setLoading(false); }
  };

  useEffect(() => { if (currentPkg && panels !== null && batteries !== null) runEstimate(); /* eslint-disable-next-line */ }, [currentPkg?.id, panels, batteries]);

  return (
    <div className="pe-mobile-cta-safe" data-testid="page-konfigurator">
      <div className="pe-container pt-10 pb-6">
        <div className="pe-eyebrow">Konfigurator</div>
        <h1 className="mt-2 font-display font-bold text-3xl lg:text-4xl">Rakit konfigurasi dan lihat estimasinya</h1>
        <p className="mt-3 text-[color:var(--pe-text-muted)] max-w-2xl">Pilih paket dasar, lalu sesuaikan panel dan baterai. Backend menghitung ulang total dari katalog — harga tidak diambil dari browser.</p>
      </div>

      <div className="pe-container mt-6 grid lg:grid-cols-12 gap-6">
        <div className="lg:col-span-8">
          <Tabs value={mode} onValueChange={setMode}>
            <TabsList data-testid="config-mode-tabs">
              <TabsTrigger value="guided" data-testid="tab-guided"><Boxes size={14} className="mr-1" /> Terbimbing</TabsTrigger>
              <TabsTrigger value="detail" data-testid="tab-detail"><Settings size={14} className="mr-1" /> Detail</TabsTrigger>
            </TabsList>

            <TabsContent value="guided">
              <div className="pe-card mt-4">
                <div className="grid sm:grid-cols-3 gap-4">
                  <div>
                    <Label>Daya PLN</Label>
                    <Select value={String(va)} onValueChange={(v) => setVa(Number(v))}>
                      <SelectTrigger className="mt-2" data-testid="config-select-va"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        {availableVas.map((v) => <SelectItem key={v} value={String(v)}>{v} VA</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label>Tier</Label>
                    <Select value={tier} onValueChange={setTier}>
                      <SelectTrigger className="mt-2" data-testid="config-select-tier"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="Starter">Starter</SelectItem>
                        <SelectItem value="Safer">Safer</SelectItem>
                        <SelectItem value="Signature">Signature</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label>Fase</Label>
                    <div className="mt-2 pe-chip">{currentPkg?.phase || "—"}</div>
                  </div>
                </div>

                {currentPkg && (
                  <div className="mt-6 grid sm:grid-cols-2 gap-4">
                    <div>
                      <Label>Jumlah panel ({currentPkg.panel_wp} Wp/unit)</Label>
                      <Input type="number" min={2} value={panels || ""} onChange={(e) => setPanels(Number(e.target.value))} className="mt-2" data-testid="config-input-panels" />
                      <p className="text-xs text-[color:var(--pe-text-muted)] mt-1">Baseline paket: {currentPkg.panels} panel · {currentPkg.kwp} kWp</p>
                    </div>
                    <div>
                      <Label>Jumlah modul baterai ({currentPkg.battery_kwh_per_unit} kWh/modul)</Label>
                      <Input type="number" min={0} value={batteries || 0} onChange={(e) => setBatteries(Number(e.target.value))} className="mt-2" data-testid="config-input-batteries" />
                      <p className="text-xs text-[color:var(--pe-text-muted)] mt-1">Baseline paket: {currentPkg.battery_units} unit · {currentPkg.battery_nominal_kwh.toFixed(2)} kWh nominal</p>
                    </div>
                  </div>
                )}
              </div>
            </TabsContent>

            <TabsContent value="detail">
              <div className="pe-card mt-4">
                <h3 className="font-display font-semibold text-lg">Mode Detail — Bill of Material</h3>
                <p className="text-sm text-[color:var(--pe-text-muted)] mt-1">Rincian item wajib sesuai template engineering. Harga satuan per item (panel, inverter, baterai, MCB, SPD, kabel, rail, labor) belum lengkap pada katalog aktif — status "data belum lengkap".</p>
                <ul className="mt-5 space-y-2 text-sm">
                  {["Panel surya", "Inverter hybrid", "Modul baterai", "MCB/MCCB AC & DC", "SPD AC & DC", "Isolator/fuse", "Kabel AC & DC", "Konektor", "Grounding & aksesori", "Rail, clamp, bracket, baut", "Panel box & monitoring", "Instalasi, pengujian, commissioning", "Transportasi & logistik"].map((it) => (
                    <li key={it} className="flex items-center justify-between py-2 border-b border-slate-100 last:border-0">
                      <span>{it}</span>
                      <span className="text-xs pe-chip" style={{ background: "var(--pe-surface)" }}>Perlu data admin</span>
                    </li>
                  ))}
                </ul>
                <div className="mt-5 p-3 rounded-lg text-xs" style={{ background: "var(--pe-cyan-soft)", color: "var(--pe-primary)" }}>
                  Status: template BOM dan harga satuan menunggu input admin. Mode Terbimbing tetap bisa dipakai untuk estimasi berbasis paket referensi + allowance panel/baterai.
                </div>
              </div>
            </TabsContent>
          </Tabs>

          {currentPkg && (
            <div className="pe-card mt-6">
              <div className="pe-eyebrow">Spesifikasi hasil</div>
              <div className="mt-3 grid sm:grid-cols-3 gap-4 text-sm">
                <Stat k="Kapasitas PV" v={`${((panels || 0) * currentPkg.panel_wp / 1000).toFixed(2)} kWp`} />
                <Stat k="Inverter" v={`${currentPkg.inverter_kw} kW`} />
                <Stat k="Baterai nominal" v={`${((batteries || 0) * currentPkg.battery_kwh_per_unit).toFixed(2)} kWh`} />
              </div>
            </div>
          )}
        </div>

        {/* Sticky price summary */}
        <aside className="lg:col-span-4">
          <div className="pe-card lg:sticky lg:top-24" data-testid="price-summary">
            <div className="pe-eyebrow">Ringkasan estimasi</div>
            {!estimate && <p className="text-sm text-[color:var(--pe-text-muted)] mt-3">Pilih paket untuk melihat estimasi.</p>}
            {estimate && (
              <>
                <div className="mt-4 space-y-2 text-sm">
                  <Line k="Paket referensi" v={fmtRp(estimate.price_breakdown.paket_referensi_rp)} />
                  {estimate.price_breakdown.panel_addon_rp !== 0 && <Line k={`Addon panel (${estimate.panels - currentPkg.panels > 0 ? '+' : ''}${estimate.panels - currentPkg.panels})`} v={fmtRp(estimate.price_breakdown.panel_addon_rp)} />}
                  {estimate.price_breakdown.battery_addon_rp !== 0 && <Line k={`Addon baterai (${estimate.battery_units - currentPkg.battery_units > 0 ? '+' : ''}${estimate.battery_units - currentPkg.battery_units})`} v={fmtRp(estimate.price_breakdown.battery_addon_rp)} />}
                </div>
                <div className="mt-4 pt-4 border-t" style={{ borderColor: "var(--pe-border)" }}>
                  <div className="text-xs text-[color:var(--pe-text-muted)]">{estimate.price_label}</div>
                  <div className="font-mono-spec font-bold text-2xl" style={{ color: "var(--pe-primary)" }} data-testid="price-summary-total">
                    {fmtRp(estimate.total_rp)}
                  </div>
                </div>
                <div className="mt-4">
                  {estimate.status === "sesuai_aturan_katalog" ? (
                    <div className="flex items-center gap-2 text-xs text-emerald-700" data-testid="status-badge">
                      <CheckCircle2 size={14} /> Sesuai aturan katalog (bukan persetujuan akhir instalasi)
                    </div>
                  ) : (
                    <div className="flex items-center gap-2 text-xs text-amber-700" data-testid="status-badge">
                      <AlertTriangle size={14} /> Perlu verifikasi engineering
                    </div>
                  )}
                  {estimate.warnings?.map((w, i) => (
                    <div key={i} className="mt-2 text-xs text-amber-700">• {w}</div>
                  ))}
                </div>
                <p className="mt-4 text-xs text-[color:var(--pe-text-muted)]">{estimate.price_breakdown.note}</p>
                <div className="mt-5 flex flex-col gap-2">
                  <Link to="/rencana-tumbuh" className="pe-btn-ghost justify-center text-sm" data-testid="config-cta-rencana">
                    <Sprout size={14} /> Lihat Rencana Tumbuh
                  </Link>
                  <a
                    href={`https://wa.me/6285121341168?text=${encodeURIComponent(`Halo Proper Energy, saya tertarik konfigurasi ${tier} ${va} VA (${estimate.kwp} kWp, baterai ${estimate.battery_nominal_kwh.toFixed(2)} kWh), estimasi ${fmtRp(estimate.total_rp)}.`)}`}
                    target="_blank" rel="noopener noreferrer"
                    className="pe-btn-primary justify-center text-sm"
                    data-testid="config-cta-wa"
                  >
                    <MessageCircle size={14} /> Konsultasi penawaran
                  </a>
                </div>
              </>
            )}
          </div>
        </aside>
      </div>
    </div>
  );
}

const Line = ({ k, v }) => (<div className="flex justify-between"><span className="text-[color:var(--pe-text-muted)]">{k}</span><span className="font-mono-spec">{v}</span></div>);
const Stat = ({ k, v }) => (<div><div className="text-xs text-[color:var(--pe-text-muted)]">{k}</div><div className="font-mono-spec text-lg font-semibold">{v}</div></div>);
